import { getSkinfoldProtocol, ageAt } from './bodyComposition';
import { BMI_ADULT_AGE, bmiClass, bmiRuler, bodyFatRuler, waistHeightRuler, waistHipRuler } from './bodyIndicatorRanges';
import {
  BMI_KEY,
  SKINFOLD_FAT_KEY,
  WAIST_HEIGHT_KEY,
  WAIST_HIP_KEY,
  computeComposition,
  computeDelta,
  computeWaistHeight,
  computeWaistHip,
  formatDate,
  formatDelta,
  formatMeasure,
  formatNumber,
} from './bodyMeasurementsMath';

/**
 * Cartões dos indicadores calculados (IMC classificado, relações
 * cintura-quadril e cintura-estatura, gordura pelas dobras). Mesmo formato
 * de `buildIndicators`: { key, label, value, delta, ruler, note, details, selectable }.
 * `subject`: { variant, birthday } do paciente.
 */

function ratioDelta(value, referenceValue, decimals) {
  const delta = computeDelta(value, referenceValue);
  if (delta === null) return null;
  if (Math.abs(delta) < Math.pow(10, -decimals) / 2) return '=';
  return `${delta > 0 ? '+' : '-'}${formatNumber(Math.abs(delta), decimals)}`;
}

/**
 * IMC com a faixa da OMS (na legenda da régua) e o risco (na nota); abaixo de
 * 20 anos não classifica. Peso ou altura implausíveis: avisa e não calcula.
 */
export function buildBmiIndicator(bmi, referenceBmi, current, subject) {
  if (!bmi) return null;
  const heightNote = bmi.heightFrom ? `Com a altura de ${formatDate(bmi.heightFrom)}` : null;

  if (bmi.value === null) {
    return { key: BMI_KEY, label: 'IMC', value: '—', delta: null, ruler: null, note: `${bmi.warning}: IMC não calculado`, selectable: true };
  }

  const age = subject ? ageAt(subject.birthday, current.measuredAt) : null;
  const isMinor = age !== null && age < BMI_ADULT_AGE;
  const band = isMinor ? null : bmiClass(bmi.value);
  const notes = [
    band ? band.risk : null,
    isMinor ? 'Abaixo de 20 anos: avaliar pelas curvas de crescimento' : null,
    heightNote,
  ].filter(Boolean);

  return {
    key: BMI_KEY,
    label: 'IMC',
    value: formatNumber(bmi.value, 1),
    delta: referenceBmi && referenceBmi.value !== null ? ratioDelta(bmi.value, referenceBmi.value, 1) : null,
    ruler: band ? bmiRuler(bmi.value) : null,
    note: notes.join(' · ') || null,
    selectable: true,
  };
}

/** Relações cintura-quadril e cintura-estatura, quando há as medidas. */
export function buildRatioIndicators(history, current, reference, subject) {
  const items = [];
  const variant = subject ? subject.variant : null;

  const waistHip = computeWaistHip(current);
  if (waistHip !== null) {
    const ruler = waistHipRuler(waistHip, variant);
    items.push({
      key: WAIST_HIP_KEY,
      label: 'Cintura-quadril',
      value: formatNumber(waistHip, 2),
      delta: reference ? ratioDelta(waistHip, computeWaistHip(reference), 2) : null,
      ruler,
      note: ruler ? null : 'Informe o sexo no cadastro para classificar',
      selectable: true,
    });
  }

  const waistHeight = computeWaistHeight(history, current);
  if (waistHeight !== null) {
    items.push({
      key: WAIST_HEIGHT_KEY,
      label: 'Cintura-estatura',
      value: formatNumber(waistHeight, 2),
      delta: reference ? ratioDelta(waistHeight, computeWaistHeight(history, reference), 2) : null,
      ruler: waistHeightRuler(waistHeight),
      note: null,
      selectable: true,
    });
  }

  return items;
}

/**
 * % de gordura pelas dobras, com massa gorda e magra. Quando falta algo
 * (sexo, nascimento, dobra), o cartão aparece sem número e diz o que falta.
 */
export function buildCompositionIndicator(current, reference, subject) {
  const composition = computeComposition(current, subject);
  if (!composition) return null;
  const protocol = getSkinfoldProtocol(current.skinfoldProtocol);
  const base = { key: SKINFOLD_FAT_KEY, label: 'Gordura pelas dobras', selectable: false };

  if (!composition.ok) {
    return { ...base, value: '—', delta: null, ruler: null, note: composition.reason };
  }

  const previous = reference ? computeComposition(reference, subject) : null;
  const details = composition.fatMass !== null
    ? [`Massa gorda ${formatMeasure(composition.fatMass, 'weight')}`, `Massa magra ${formatMeasure(composition.leanMass, 'weight')}`]
    : ['Registre o peso para ver massa gorda e magra'];

  return {
    ...base,
    value: formatMeasure(composition.bodyFat, 'body_fat'),
    delta: previous && previous.ok ? formatDelta(computeDelta(composition.bodyFat, previous.bodyFat), 'body_fat') : null,
    ruler: bodyFatRuler(composition.bodyFat, subject ? subject.variant : null),
    note: `${protocol ? protocol.label : ''} · densidade ${formatNumber(composition.density, 4)} g/mL`,
    details,
  };
}
